import { PickupScheduleComponent } from "@/app/infrastructures/dependencies/PickupScheduleComponent";
import {
  PickupCorporateAddress,
  PickupSchedule,
  PickupScheduleList,
  RequestPickupScheduleList,
} from "@/domain/entities/PickupSchedule";
import store from "@/store";
import { container } from "tsyringe";
import {
  Action,
  Module,
  Mutation,
  VuexModule,
  getModule,
} from "vuex-module-decorators";
import { PickupSchedulePresenter } from "../presenters/PickupSchedulePresenter";
import {
  PickupCorporateAddressRequest,
  PickupScheduleApiRequest,
  PickupScheduleFormApiRequest,
} from "@/data/payload/api/PickupScheduleApiRequest";
import {
  ApiRequestList,
  ModalMessageEntities,
} from "@/domain/entities/MainApp";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { MainAppController } from "./MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import i18nInstance from "@/i18n/index";
import { AccountController } from '@/app/ui/controllers/AccountController';

interface PickupScheduleState {
  pickupScheduleList: PickupScheduleList;
  pickupScheduleData: PickupSchedule;
  isLoading: boolean;
}
PickupScheduleComponent.init();
@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: "pickup-schedule",
})
class PickupScheduleStore extends VuexModule implements PickupScheduleState {
  public pickupScheduleList: PickupScheduleList = new PickupScheduleList();
  public pickupScheduleData: PickupSchedule = new PickupSchedule();
  public listPickupCorporateAddress: PickupCorporateAddress[] = [];
  public selectedAddress = new PickupCorporateAddress();
  public isLoading = false;
  public isSuccessAddAddress = false;
  public pickupCorporateAddessLimit = 50;
  public pickupCorporateAddressRequest = new ApiRequestList({
    page: 1,
    limit: 50,
    isTotalData: false,
    selectAddress: false,
  });

  @Action
  public getPickupScheduleList(params: RequestPickupScheduleList) {
    const presenter = container.resolve(PickupSchedulePresenter);
    return presenter.getPickupScheduleList(params);
  }

  @Action
  public getDetailPickupSchedule(id: any) {
    const presenter = container.resolve(PickupSchedulePresenter);
    return presenter.getDetailPickupSchedule(id);
  }

  @Action
  public updateStatusPickupSchedule(params: {
    payload: PickupScheduleApiRequest;
    id: any;
  }) {
    const presenter = container.resolve(PickupSchedulePresenter);
    return presenter.updateStatusPickupSchedule(params.payload, params.id);
  }

  @Action
  public getCourierVehicleList(search: string) {
    const presenter = container.resolve(PickupSchedulePresenter);
    return presenter.getCourierVehicleList(search);
  }

  @Action({ rawError: true })
  public createPickupSchedule(payload: PickupScheduleFormApiRequest) {
    const presenter = container.resolve(PickupSchedulePresenter);

    return presenter.createPickupSchedule(payload);
  }

  @Action({ rawError: true })
  public updatePickupSchedule(params: {
    payload: PickupScheduleFormApiRequest;
    id: number;
  }) {
    const presenter = container.resolve(PickupSchedulePresenter);

    return presenter.updatePickupSchedule(params.payload, params.id);
  }

  @Action
  public async getListPickupCorporateAddress(params: ApiRequestList) {
    i18nInstance.global.locale.value = AccountController.accountData.account_type_detail.countryCode.toLowerCase();
    const t = i18nInstance.global.t;
    this.setPickupCorporateAddressRequest(params);
    try {
      MainAppController.showLoading();

      const { selectAddress, ...rest } = params;

      const presenter = container.resolve(PickupSchedulePresenter);
      const res: ResponsePayloadV2 = await presenter.getListPickupCorporateAddress(
        new ApiRequestList(rest)
      );
      this.setListPickupCorporateAddress(res.data);
      this.setPickupCorporateAddressLimit(res.pagination.limit);

      if (selectAddress) {
        const selectedAddress = new PickupCorporateAddress(
          res.data.find((e: PickupCorporateAddress) => e.pcaIsDefault)
        );
        if (selectedAddress.pcaId) {
          this.setSelectedAddressPickupCorporateAddress(selectedAddress);
        } else {
          this.setSelectedAddressPickupCorporateAddress(
            new PickupCorporateAddress()
          );
        }
      } else {
        if (this.selectedAddress.pcaId) {
          const currentSelectedAddress = new PickupCorporateAddress(
            res.data.find(
              (e: PickupCorporateAddress) =>
                e.pcaId === this.selectedAddress.pcaId
            )
          );
          this.setSelectedAddressPickupCorporateAddress(currentSelectedAddress);
        }
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, t("pickupCorporate.errorGetData"), () =>
          this.getListPickupCorporateAddress(params)
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  @Action
  public async setPickupCorporateAddressDefault(
    data: PickupCorporateAddress
  ): Promise<boolean> {
    i18nInstance.global.locale.value = AccountController.accountData.account_type_detail.countryCode.toLowerCase();
    const t = i18nInstance.global.t;
    MainAppController.showLoading();
    const presenter = container.resolve(PickupSchedulePresenter);

    try {
      await presenter.setPickupCorporateAddressDefault(data.pcaId);
      this.setDefaultPickupCorporateAddress(data.pcaId);
      return true;
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          t("pickupCorporate.errorSystem"),
          () => this.setPickupCorporateAddressDefault(data)
        )
      );
      return false;
    } finally {
      MainAppController.closeLoading();
    }
  }

  @Action
  public async deletePickupCorporateAddressDefault(
    data: PickupCorporateAddress
  ): Promise<boolean> {
    i18nInstance.global.locale.value = AccountController.accountData.account_type_detail.countryCode.toLowerCase();
    const t = i18nInstance.global.t;
    MainAppController.showLoading();
    const presenter = container.resolve(PickupSchedulePresenter);

    try {
      await presenter.deletePickupCorporateAddressDefault(data.pcaId);
      await this.getListPickupCorporateAddress(
        new ApiRequestList({
          ...this.pickupCorporateAddressRequest,
          selectAddress: data.pcaId === this.selectedAddress.pcaId,
        })
      );
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          title: t("pickupCorporate.successDeleteAddressTitle", {
            name: data.pcaName,
          }),
          message: t("pickupCorporate.successDeleteAddressMessage"),
          textSuccess: t("pickupCorporate.okeMengerti"),
          onSubmit: () => {
            MainAppController.closeMessageModal();
          },
          image: "badge-success",
          imageWidth: "36",
        })
      );
      return true;
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          t("pickupCorporate.errorSystem"),
          () => this.deletePickupCorporateAddressDefault(data)
        )
      );
      return false;
    } finally {
      MainAppController.closeLoading();
    }
  }

  @Action
  public async addPickupCorporateAddress(data: PickupCorporateAddressRequest) {
    i18nInstance.global.locale.value = AccountController.accountData.account_type_detail.countryCode.toLowerCase();
    const t = i18nInstance.global.t;
    MainAppController.showLoading();
    const presenter = container.resolve(PickupSchedulePresenter);
    await presenter
      .addPickupCorporateAddress(data)
      .then(async () => {
        await this.getListPickupCorporateAddress(
          new ApiRequestList({
            ...this.pickupCorporateAddressRequest,
            selectAddress: data.pcaIsDefault || !this.selectedAddress.pcaId,
          })
        );
        MainAppController.closeLoading();
        this.setSuccessAddAddress(true);
      })
      .catch((error: any) => {
        MainAppController.closeLoading();
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, t("pickupCorporate.errorSystem"))
        );
      });
  }

  @Action
  public async updatePickupCorporateAddress(params: {
    data: PickupCorporateAddressRequest;
    id: number;
  }) {
    i18nInstance.global.locale.value = AccountController.accountData.account_type_detail.countryCode.toLowerCase();
    const t = i18nInstance.global.t;
    MainAppController.showLoading();
    const presenter = container.resolve(PickupSchedulePresenter);
    await presenter
      .updatePickupCorporateAddress(params.data, params.id)
      .then(async () => {
        await this.getListPickupCorporateAddress(
          new ApiRequestList({
            ...this.pickupCorporateAddressRequest,
            selectAddress: params.data.pcaIsDefault,
          })
        );
        MainAppController.closeLoading();
        this.setSuccessAddAddress(true);
      })
      .catch((error: any) => {
        MainAppController.closeLoading();
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, t("pickupCorporate.errorSystem"))
        );
      });
  }

  @Mutation
  public setIsLoading(bool: boolean): void {
    this.isLoading = bool;
  }

  @Mutation
  public setPickupSchedulData(data: PickupSchedule): void {
    this.pickupScheduleData = data;
  }

  @Mutation
  public setListPickupCorporateAddress(data: PickupCorporateAddress[]): void {
    this.listPickupCorporateAddress = data;
  }

  @Mutation
  public setSelectedAddressPickupCorporateAddress(
    data: PickupCorporateAddress
  ): void {
    this.selectedAddress = data;
  }

  @Mutation
  public setDefaultPickupCorporateAddress(id: number): void {
    this.listPickupCorporateAddress = this.listPickupCorporateAddress.map(
      address =>
        new PickupCorporateAddress({
          ...address,
          pcaIsDefault: address.pcaId === id,
        })
    );
    this.selectedAddress =
      this.listPickupCorporateAddress.find(address => address.pcaId === id) ||
      new PickupCorporateAddress();
  }

  @Mutation
  public setSuccessAddAddress(bool: boolean): void {
    this.isSuccessAddAddress = bool;
  }

  @Mutation
  public setPickupCorporateAddressLimit(value: number): void {
    this.pickupCorporateAddessLimit = value;
  }

  @Mutation
  public setPickupCorporateAddressRequest(value: ApiRequestList): void {
    this.pickupCorporateAddressRequest = new ApiRequestList(value);
  }
}

export const PickupScheduleController = getModule(PickupScheduleStore);
